import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { get, isEmpty, isArray } from 'lodash';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { checkArray } from '../../../../Components/helper';
import ConfirmationPopup from '../../../../Components/ModalPopup/ConfirmationPopup';
import * as action from '../store/CustomSections.action';
import AddCustomColumnTemplateModal from './AddCustomColumnTemplateModal';
import { useCustomColumnState } from '../CustomColumnContextProvider';

import CustomCardDrag from './CustomCardDrag';
import { useCostingAccess } from '../../../../Components/CustomHooks/useCostingAccess';
import { hideCostingColumns, mergeVisibleOrder } from '../../ProductList/utils/costing-columns';

const CustomColumnTableList = forwardRef(({ searchText }, ref) => {
  const reduxData = useSelector((prevState) => prevState);
  const {
    customSectionData: { customColumnList, currSectionList },
  } = reduxData;
  const hasCostingAccess = useCostingAccess();
  const { sectionId } = useParams();
  const { t } = useTranslation('common');
  const { setCustomColumnState } = useCustomColumnState();
  const [isDrag, setIsDrag] = useState(false);

  const dispatch = useDispatch();
  const [state, setState] = useState({
    listData: [],
    deleteModalState: false,
    deleteId: null,
    addColumnModalState: false,
    sectionName: '',
  });
  const { deleteModalState, listData, deleteId, addColumnModalState, sectionName } = state;

  useImperativeHandle(ref, () => ({
    getColumnSectionState() {
      return state;
    },
    resetSectionName(value) {
      setState((prev) => ({ ...prev, sectionName: value }));
    },
  }));

  useEffect(() => {
    if (isArray(customColumnList?.columns)) {
      const loadedColumns = isArray(currSectionList) ? currSectionList.flatMap((section) => section?.columns || []) : [];
      let arrayList = hideCostingColumns(customColumnList?.columns, hasCostingAccess, loadedColumns);
      if (searchText) {
        arrayList = arrayList.filter((val) => val?.title.toLowerCase().indexOf(searchText.toLowerCase()) > -1);
      }
      setState((prevState) => ({ ...prevState, listData: arrayList }));
    }
  }, [searchText, customColumnList, currSectionList, hasCostingAccess]);

  useEffect(() => {
    if (!isEmpty(customColumnList)) {
      setState((prevState) => ({
        ...prevState,
        sectionName: customColumnList?.name,
      }));
    }
  }, [customColumnList]);

  const toggleDeleteModal = (boolValue, id) => {
    if (id) {
      setState((prevState) => ({
        ...prevState,
        deleteId: id,
        deleteModalState: boolValue,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        deleteId: null,
        deleteModalState: boolValue,
      }));
    }
  };

  const deleteConfirmed = async () => {
    if (deleteId) {
      const res = await dispatch(action.deleteSectionColumn(deleteId, sectionId));
      if (res) {
        toggleDeleteModal(false);
      }
    }
  };

  const redirectEditPage = async (column) => {
    await dispatch(action.setEditData(column));
    handleModalState(true);
  };

  const handleModalState = (modalState) => {
    setState((preState) => ({
      ...preState,
      addColumnModalState: modalState,
    }));
    if (!modalState)
      setCustomColumnState((prevState) => ({
        ...prevState,
        isEditMode: false,
        columns: [
          {
            columnType: null,
            title: '',
          },
        ],
      }));
  };

  const handleChange = (value) => {
    setState((preState) => ({
      ...preState,
      sectionName: value,
    }));
  };

  const renderTr = useCallback(
    (item, index) => {
      return (
        <CustomCardDrag
          toggleDeleteModal={toggleDeleteModal}
          redirectEditPage={redirectEditPage}
          key={item?._id}
          id={item?.id}
          column={item}
          index={index}
          moveTr={moveTr}
        />
      );
    },
    [listData],
  );

  function swapArrayLocs(arr, index1, index2) {
    const temp = arr[index1];
    arr[index1] = arr[index2];
    arr[index2] = temp;
    return arr;
  }

  const moveTr = (dragIndex, hoverIndex) => {
    setIsDrag(true);
    const currList = swapArrayLocs(listData, dragIndex, hoverIndex);
    setState((prevCards) => ({
      ...prevCards,
      listData: currList,
    }));
  };

  useEffect(() => {
    if (isArray(listData) && listData.length > 0 && isDrag && sectionId) {
      setIsDrag(false);
      const orderedColumns = isArray(customColumnList?.columns) ? mergeVisibleOrder(customColumnList.columns, listData) : listData;
      const newSequence = [];
      orderedColumns.forEach((item, index) => {
        newSequence.push({ _id: item?._id, position: index });
      });
      dispatch(action.updateSequenceForCustomSection(sectionId, newSequence));
    }
  }, [listData, isDrag, dispatch, sectionId, customColumnList]);
  return (
    <>
      <div className="d-flex flex-wrap mb-2">
        <div className="input-group form-floating-label fixed-label pt-0 w-xs-100 w-300">
          <input type="text" value={sectionName} maxLength="30" onChange={(e) => handleChange(e.target.value)} className="form-control text-bordered" />

          <div className="text-count">{get(state, 'sectionName', '').length}/30</div>
        </div>
        <div className="d-md-none break mb-2" />
        <button className="btn btn-default ml-md-auto" onClick={() => handleModalState(true)}>
          {t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CUSTOM_COLUMN_TABLE.ADD_COLUMNS_BUTTON')}
        </button>
      </div>
      <div className="table-responsive">
        <table className="table common-custom-table mt-4">
          <thead>
            <tr>
              <th className="w-500px">{t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CUSTOM_COLUMN_TABLE.TEMPLATE_NAME')}</th>
              <th>{t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CUSTOM_COLUMN_TABLE.COLUMN_TYPE')}</th>
              <th>{t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CUSTOM_COLUMN_TABLE.TYPE')}</th>
              <th>{t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CUSTOM_COLUMN_TABLE.ACTION')}</th>
            </tr>
          </thead>
          <tbody>
            <DndProvider backend={HTML5Backend}>
              {checkArray(listData) ? (
                listData?.map((column, i) => {
                  return renderTr(column, i);
                })
              ) : (
                <td colSpan={4} className="text-center">
                  {t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CUSTOM_COLUMN_TABLE.NO_RECORDS')}
                </td>
              )}
            </DndProvider>
          </tbody>
        </table>
      </div>
      {deleteModalState && (
        <ConfirmationPopup
          onOpen={deleteModalState}
          onConfirm={() => deleteConfirmed()}
          title={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CUSTOM_COLUMN_TABLE.DELETE_SECTION_TITLE')}
          message={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CUSTOM_COLUMN_TABLE.DELETE_SECTION_MESSAGE')}
          toggle={() => {
            toggleDeleteModal(!deleteModalState);
          }}
          loading={false}
        />
      )}
      {addColumnModalState && <AddCustomColumnTemplateModal isOpen={addColumnModalState} handleModalState={handleModalState} />}
    </>
  );
});

export default CustomColumnTableList;
